import React, { useState } from "react";
import { Info, TrendingUp } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@cms/ui/components/card";
import { Badge } from "@cms/ui/components/badge";
import { Button } from "@cms/ui/components/button";
import { Alert, AlertTitle, AlertDescription } from "@cms/ui/components/alert";
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from "@cms/ui/components/table";
import type { BillingForecast } from "@cms/types";
import { formatPence } from "@cms/portal-lib/format";
import { ROLLING_ESTIMATE_BANNER } from "@cms/portal-lib/billing";

const COLLAPSED_MONTHS = 12;

/**
 * The 60-month payment forecast: fixed months show locked amounts, rolling
 * months are flagged as estimates (global banner + per-value icon).
 */
export function BillingForecastCard({ forecast }: { forecast: BillingForecast }) {
  const [showAll, setShowAll] = useState(false);
  const months = showAll ? forecast.months : forecast.months.slice(0, COLLAPSED_MONTHS);
  const hasRolling = forecast.months.some((m) => m.estimate);

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Payment forecast</CardTitle>
        <CardDescription>
          Projected {forecast.billing_cadence === "annual" ? "annual" : "monthly"} charges
          for the next {forecast.billing_cadence === "annual"
            ? `${forecast.months.length} years`
            : `${forecast.months.length} months`}, including adjustment start and expiry dates.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {hasRolling && (
          <Alert>
            <Info className="h-4 w-4" />
            <AlertTitle>Estimates ahead</AlertTitle>
            <AlertDescription>{ROLLING_ESTIMATE_BANNER}</AlertDescription>
          </Alert>
        )}

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Charge date</TableHead>
              <TableHead>Phase</TableHead>
              <TableHead>Breakdown</TableHead>
              <TableHead className="text-right">Total</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {months.map((month) => (
              <TableRow key={month.charge_date}>
                <TableCell className="font-medium">{month.charge_date}</TableCell>
                <TableCell>
                  <Badge variant={month.phase === "fixed" ? "success" : "secondary"}>
                    {month.phase === "fixed" ? "Fixed" : "Rolling"}
                  </Badge>
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {month.lines
                    .map((line) =>
                      line.adjustment_pence !== 0
                        ? `${line.description} ${formatPence(line.amount_pence)} (was ${formatPence(line.base_pence)})`
                        : `${line.description} ${formatPence(line.amount_pence)}`
                    )
                    .join(" · ")}
                  {month.vat_pence > 0 ? ` · VAT ${formatPence(month.vat_pence)}` : ""}
                </TableCell>
                <TableCell className="text-right font-medium">
                  <span className="inline-flex items-center gap-1">
                    {formatPence(month.total_pence)}
                    {month.estimate && (
                      <TrendingUp
                        className="h-3.5 w-3.5 text-muted-foreground"
                        aria-label="Estimate — subject to future standard pricing changes"
                      />
                    )}
                  </span>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {forecast.months.length > COLLAPSED_MONTHS && (
          <Button size="sm" variant="outline" onClick={() => setShowAll((v) => !v)}>
            {showAll ? "Show first year only" : `Show all ${forecast.months.length} charges`}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
